import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";

import Plus from "@assets/icons/rounded/add.svg";
import Search from "@assets/icons/rounded/search.svg";
import { useDebounce } from "@app/util/use-debounce";
import { ClickableComponent, TextFieldComponent } from "@app/core";
import { SelectComponent } from "@app/core/select";
import { useAppDispatch, useAppSelector } from "@app/redux/store";
import { ExternalDealFilterLiveDeals, externalDealFiltersActions } from "@app/redux/reducers/external-deal-filters";
import { getTranslatedPlatforms } from "@app/constants/platform";
import { FilterItem } from "@app/api/core/filter/filter-item";
import { getMerchants } from "@app/modules/deal-detail/components/api-calls";
import { mapMerchantToFilterItem } from "@app/api/core/filter/map-filter-item";
import { getTranslatedStatusses } from "@app/constants/status";
import { ROUTES } from "@app/constants/routes";
import { ReactSelect } from "@app/core/react-select/react-select";

import styles from "./external-deal-filter-filter-bar-component.module.scss";

// Select ids for the live deal buckets; 0 means no filter.
const LIVE_DEALS_OPTIONS: { id: number; value: ExternalDealFilterLiveDeals; translationKey: string }[] = [
  { id: 1, value: "0", translationKey: "externalDealFilters.overview.filters.liveDeals.none" },
  { id: 2, value: "1", translationKey: "externalDealFilters.overview.filters.liveDeals.one" },
  { id: 3, value: "2+", translationKey: "externalDealFilters.overview.filters.liveDeals.twoOrMore" }
];

const ExternalDealFilterFilterBar = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { currentSearch, currentEan, currentStatus, currentPlatform, currentMerchantFilter, currentLiveDeals } =
    useAppSelector((state) => state.externalDealFilters);
  const [search, setSearch] = useState<string>(currentSearch || "");
  const debouncedSearchTerm = useDebounce(search, 500);
  const [ean, setEan] = useState<string>(currentEan || "");
  const debouncedEan = useDebounce(ean, 500);

  const translatedPlatforms = getTranslatedPlatforms(intl);
  const translatedStatusses = getTranslatedStatusses(intl);

  useEffect(() => {
    dispatch(externalDealFiltersActions.setCurrentSearch(debouncedSearchTerm || undefined));
  }, [debouncedSearchTerm]);

  useEffect(() => {
    dispatch(externalDealFiltersActions.setCurrentEan(debouncedEan.trim() || undefined));
  }, [debouncedEan]);

  const onLiveDealsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const option = LIVE_DEALS_OPTIONS.find((liveDealsOption) => liveDealsOption.id === Number(e.target.value));
    dispatch(externalDealFiltersActions.setCurrentLiveDeals(option?.value));
  };

  const onPlatformChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value);
    dispatch(externalDealFiltersActions.setCurrentPlatform(value || undefined));
  };

  const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value);
    dispatch(externalDealFiltersActions.setCurrentStatus(value || undefined));
  };

  const onMerchantChange = (value: FilterItem | null) => {
    dispatch(externalDealFiltersActions.setCurrentMerchantFilter(value || undefined));
  };

  const loadMerchants = async (inputValue: string, callback: (options: FilterItem[]) => void) => {
    const showPlatformInLabels = !currentPlatform;
    const merchants = await getMerchants(inputValue, currentPlatform);
    callback(merchants ? merchants.map((merchant) => mapMerchantToFilterItem(merchant, showPlatformInLabels)) : []);
  };

  return (
    <div className={styles.filterBar}>
      <div className={styles.searchField}>
        <TextFieldComponent
          height={40}
          placeholder={intl.formatMessage({ id: "externalDealFilters.overview.filters.search.placeholder" })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          type="text"
          id="search"
          icon={Search}
          width={318}
        />
      </div>
      <div className={styles.eanField}>
        <TextFieldComponent
          height={40}
          placeholder={intl.formatMessage({ id: "externalDealFilters.overview.filters.ean.placeholder" })}
          value={ean}
          onChange={(e) => setEan(e.target.value)}
          type="text"
          id="ean"
          icon={Search}
        />
      </div>

      <SelectComponent
        onChange={onStatusChange}
        value={currentStatus}
        options={[
          { id: 0, name: intl.formatMessage({ id: "general.select.status.placeholder" }) },
          ...translatedStatusses
        ]}
        autoWidth
      />
      <SelectComponent
        onChange={onPlatformChange}
        value={currentPlatform}
        options={[
          { id: 0, name: intl.formatMessage({ id: "general.select.platforms.placeholder" }) },
          ...translatedPlatforms
        ]}
        autoWidth
      />
      <SelectComponent
        onChange={onLiveDealsChange}
        value={LIVE_DEALS_OPTIONS.find((option) => option.value === currentLiveDeals)?.id ?? 0}
        options={[
          { id: 0, name: intl.formatMessage({ id: "externalDealFilters.overview.filters.liveDeals.placeholder" }) },
          ...LIVE_DEALS_OPTIONS.map((option) => ({
            id: option.id,
            name: intl.formatMessage({ id: option.translationKey })
          }))
        ]}
        autoWidth
      />
      <div className={styles.merchantSelect}>
        <ReactSelect
          id="merchant"
          isClearable
          value={currentMerchantFilter}
          placeholder={intl.formatMessage({ id: "general.multiselect.merchants.placeholder" })}
          loadOptions={loadMerchants}
          isAsync
          isMulti={false}
          onChange={onMerchantChange}
        />
      </div>

      <ClickableComponent
        title={intl.formatMessage({ id: "externalDealFilters.overview.button.add" })}
        height={40}
        icon={Plus}
        iconSize="20px"
        iconFillColor="#ffffff"
        iconPosition="left"
        href={ROUTES.EXTERNAL_DEAL_FILTERS_NEW}
      />
    </div>
  );
};

export { ExternalDealFilterFilterBar };
